import {
  Mail, Eye, CheckCircle2, XCircle, DollarSign, Pencil, Trash2,
} from "lucide-react";
import PropertyImage from "../PropertyImage";
import { Kpi, IconBtn, CommitProgress } from "./atoms";
import { fmtUSD0, pct, calcMonthlyPayment } from "./dealMath";

export default function DealCard({ deal, onEdit, onDelete, onOpen }) {
  const s = deal.stats || {};
  // Derive DSCR client-side so we don't have to backfill old docs:
  // pmt = monthly payment from existing loan fields, DSCR = NOI / (pmt × 12)
  const dscr = (() => {
    const pmt = calcMonthlyPayment({
      principal: deal.loan_amount,
      annualRatePct: deal.interest_rate,
      termYears: deal.amortization_years,
      loanType: deal.loan_type,
    });
    const annual = pmt * 12;
    if (!Number(deal.noi) || annual <= 0) return null;
    return Number(deal.noi) / annual;
  })();
  // Cap Rate is auto-computed on the form (NOI ÷ Purchase Price) and not
  // stored as deal.cap_rate, so we derive it here too. Falls back to the
  // stored value if one exists for legacy / hand-entered deals.
  const capRate = (() => {
    if (Number(deal.cap_rate)) return Number(deal.cap_rate);
    const noi = Number(deal.noi);
    const price = Number(deal.purchase_price);
    if (!noi || !price) return null;
    return (noi / price) * 100;
  })();
  // Color the DSCR chip based on lender-friendliness thresholds:
  //   >= 1.25 → green (institutional standard)
  //   1.00-1.25 → amber (tight)
  //   <  1.00 → rose (NOI doesn't cover debt service)
  const dscrColor = dscr == null ? "bg-slate-50 text-slate-500 border-slate-200"
    : dscr >= 1.25 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : dscr >= 1.00 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  const txnLabel = (deal.transaction_type === "refinance") ? "REFI" : "PURCHASE";
  const propTypeLabel = (deal.property_type || "—").replace(/_/g, " ");
  return (
    <div className="rounded-sm border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow" data-testid={`deal-card-${deal.id}`}>
      <button onClick={()=>onOpen(deal)} className="block w-full text-left">
        <div className="relative">
          <PropertyImage address={deal.formatted_address || deal.address} pid={null} size="600x240" className="w-full" />
          {/* Transaction-type chip overlaid on image — quick pipeline scan */}
          <span className={`absolute top-2 left-2 text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full
            ${deal.transaction_type === "refinance"
              ? "bg-violet-600/90 text-white"
              : "bg-slate-900/80 text-white"}`}>
            {txnLabel}
          </span>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{propTypeLabel}</div>
              <h3 className="text-base font-semibold text-slate-900 mt-0.5 line-clamp-1">{deal.name}</h3>
              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{deal.formatted_address || deal.address}</div>
            </div>
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border
              ${deal.status === "open" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
              {deal.status || "draft"}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
            <Kpi label="Price" value={fmtUSD0(deal.purchase_price)} />
            <Kpi label="Cap" value={pct(capRate)} />
            <Kpi label="IRR" value={pct(deal.projected_irr)} />
            <Kpi label="Equity" value={fmtUSD0(deal.equity_required)} />
          </div>
          {/* DSCR + commit progress strip — gives investors-at-a-glance signal */}
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${dscrColor}`}
              data-testid={`deal-card-dscr-${deal.id}`}
              title="DSCR · NOI ÷ Annual Debt Service">
              DSCR {dscr != null ? `${dscr.toFixed(2)}x` : "—"}
            </span>
            {deal.equity_required > 0 && s.committed_amount > 0 && (
              <CommitProgress committed={s.committed_amount} target={deal.equity_required} />
            )}
          </div>
          <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-slate-500">
            <span title="Invited"><Mail className="inline w-3 h-3 mr-1" />{s.invited || 0}</span>
            <span title="Viewed"><Eye className="inline w-3 h-3 mr-1" />{s.viewed || 0}</span>
            <span title="Interested" className="text-emerald-700"><CheckCircle2 className="inline w-3 h-3 mr-1" />{s.interested || 0}</span>
            <span title="Committed" className="text-indigo-700"><DollarSign className="inline w-3 h-3 mr-1" />{fmtUSD0(s.committed_amount)}</span>
            <span title="Pass" className="text-rose-700"><XCircle className="inline w-3 h-3 mr-1" />{s.passed || 0}</span>
          </div>
        </div>
      </button>
      <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-end gap-1">
        <IconBtn onClick={()=>onEdit(deal)} title="Edit" testid={`deal-card-edit-${deal.id}`}><Pencil className="w-3.5 h-3.5" /></IconBtn>
        <IconBtn onClick={()=>onDelete(deal)} title="Delete" danger testid={`deal-card-delete-${deal.id}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
      </div>
    </div>
  );
}
